/**
 * Alpha Workwear product page.
 *
 * Progressive enhancement only. Everything this file does has a server-rendered
 * equivalent that works without it:
 *
 *   - the variant pills are real links to ?variant=<id>, so a click without JS
 *     reloads the page onto the right variant;
 *   - the media stage is a CSS scroll-snap rail, so a phone swipes it natively;
 *   - the specification panels are native <details>;
 *   - the sticky buy bar ships with the `hidden` attribute and is only revealed
 *     here, so nobody ever meets a control that cannot work.
 *
 * There is no section-render round trip. Every figure the page can show is in
 * one JSON blob, formatted by Liquid with the shop's own money filter, so a
 * variant swap is instant and can never disagree with the server's formatting.
 */

if (!customElements.get('alpha-product')) {
  customElements.define(
    'alpha-product',
    class AlphaProduct extends HTMLElement {
      connectedCallback() {
        this.variants = this.readVariants();
        this.productUrl = this.dataset.url || window.location.pathname;

        this.stage = this.querySelector('[data-alpha-stage]');
        this.slides = Array.from(this.querySelectorAll('[data-alpha-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-alpha-thumb]'));
        this.swatches = Array.from(this.querySelectorAll('[data-alpha-swatch]'));

        this.selection = this.readSelection();

        this.bindGallery();
        this.bindZoom();
        this.bindQuantity();
        this.bindSwatches();
        this.bindStickyBar();
        this.openOnActiveMedia();
      }

      /**
       * A scroll rail cannot be given a starting position in CSS, so a page
       * loaded on ?variant=<id> renders showing slide one. Liquid has already
       * marked which thumbnail belongs to that variant; jump to it without an
       * animation, before anyone has had a chance to look at the wrong photo.
       */
      openOnActiveMedia() {
        const active = this.thumbs.find((thumb) => thumb.classList.contains('is-active'));
        if (!active || active === this.thumbs[0]) return;

        this.showMedia(active.dataset.mediaId, true);
      }

      disconnectedCallback() {
        if (this.stickyObserver) this.stickyObserver.disconnect();
        this.closeZoom();
      }

      /* ------------------------------------------------------------- data */

      readVariants() {
        const node = this.querySelector('script[data-alpha-variants]');
        if (!node) return [];

        try {
          const parsed = JSON.parse(node.textContent);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          // A malformed blob must not take the page down with it: the
          // server-rendered price, stock and pills are already correct, and the
          // pills fall back to being ordinary links.
          console.error('alpha-product: could not read variant data', error);
          return [];
        }
      }

      readSelection() {
        const selection = [];

        this.swatches.forEach((swatch) => {
          const position = parseInt(swatch.dataset.optionPosition, 10);
          if (!position) return;
          if (swatch.getAttribute('aria-current') === 'true') {
            selection[position - 1] = swatch.dataset.optionValue;
          }
        });

        return selection;
      }

      /** The variant whose options match `selection` exactly, or null. */
      variantFor(selection) {
        return (
          this.variants.find((variant) =>
            selection.every((value, index) => variant.options[index] === value)
          ) || null
        );
      }

      /**
       * Every variant that would result from swapping one option of the current
       * selection. Used both for the struck-through state and for keeping each
       * pill's href pointed at the variant it actually leads to.
       */
      candidatesFor(position, value) {
        const index = position - 1;

        return this.variants.filter((variant) => {
          if (variant.options[index] !== value) return false;

          return this.selection.every((selected, i) => {
            if (i === index) return true;
            if (selected === undefined) return true;
            return variant.options[i] === selected;
          });
        });
      }

      /* --------------------------------------------------------- pickers */

      bindSwatches() {
        if (!this.swatches.length || !this.variants.length) return;

        this.swatches.forEach((swatch) => {
          swatch.addEventListener('click', (event) => {
            // A modified click is the shopper asking for a new tab. Let the
            // browser have it - the href is a complete, valid product URL.
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

            event.preventDefault();

            const position = parseInt(swatch.dataset.optionPosition, 10);
            if (!position) return;

            this.selection[position - 1] = swatch.dataset.optionValue;
            this.refresh();
          });
        });
      }

      refresh() {
        const variant = this.variantFor(this.selection);

        this.updateSwatches();
        this.updatePrice(variant);
        this.updateSku(variant);
        this.updateStock(variant);
        this.updateForm(variant);
        this.updateUrl(variant);
        this.updateMedia(variant);
      }

      updateSwatches() {
        this.swatches.forEach((swatch) => {
          const position = parseInt(swatch.dataset.optionPosition, 10);
          const value = swatch.dataset.optionValue;
          if (!position) return;

          const selected = this.selection[position - 1] === value;
          swatch.classList.toggle('is-selected', selected);
          swatch.setAttribute('aria-current', selected ? 'true' : 'false');

          const candidates = this.candidatesFor(position, value);
          const available = candidates.some((candidate) => candidate.available);
          swatch.classList.toggle('is-unavailable', !available);

          const note = swatch.querySelector('.alpha-product__swatch-note');
          if (note) note.toggleAttribute('hidden', available);

          // Keep the link honest: it has to point at the variant this pill
          // would actually select, preferring one that is in stock.
          const target = candidates.find((candidate) => candidate.available) || candidates[0];
          if (target) swatch.setAttribute('href', target.url);
        });

        this.querySelectorAll('[data-alpha-option-selected]').forEach((node) => {
          const position = parseInt(node.dataset.alphaOptionSelected, 10);
          if (!position) return;
          const value = this.selection[position - 1];
          if (value !== undefined) node.textContent = value;
        });
      }

      updatePrice(variant) {
        const block = this.querySelector('[data-alpha-price]');
        const stickyPrice = this.querySelector('[data-alpha-sticky-price]');

        if (!variant) return;

        if (stickyPrice) stickyPrice.textContent = variant.ex;
        if (!block) return;

        const ex = block.querySelector('[data-alpha-price-ex]');
        const inc = block.querySelector('[data-alpha-price-inc]');
        const was = block.querySelector('[data-alpha-price-was]');
        const compare = block.querySelector('[data-alpha-price-compare]');
        const save = block.querySelector('[data-alpha-save]');
        const saveValue = block.querySelector('[data-alpha-save-value]');

        if (ex) ex.textContent = variant.ex;
        if (inc) inc.textContent = variant.inc;
        if (compare) compare.textContent = variant.compare;
        if (saveValue) saveValue.textContent = variant.save;

        block.classList.toggle('is-sale', Boolean(variant.sale));
        if (was) was.toggleAttribute('hidden', !variant.sale);
        if (save) save.toggleAttribute('hidden', !variant.sale);
      }

      updateSku(variant) {
        const row = this.querySelector('[data-alpha-sku-row]');
        if (!row) return;

        const value = row.querySelector('[data-alpha-sku]');
        const sku = variant && variant.sku ? variant.sku : '';

        if (value) value.textContent = sku;
        row.toggleAttribute('hidden', sku === '');
      }

      updateStock(variant) {
        const row = this.querySelector('[data-alpha-stock]');
        if (!row) return;

        const text = row.querySelector('[data-alpha-stock-text]');
        const state = variant ? variant.state : 'out';
        // No variant matches the chosen combination, so fall back to the
        // merchant's out-of-stock wording rather than leaving the previous
        // variant's "In stock" sitting under an unavailable selection.
        const message = variant ? variant.stock : row.dataset.labelUnavailable;

        ['in', 'low', 'order', 'out'].forEach((name) => {
          row.classList.toggle(`is-${name}`, name === state);
        });

        if (text && message) text.textContent = message;
      }

      updateForm(variant) {
        const input = this.querySelector('[data-alpha-variant-id]');
        const button = this.querySelector('[data-alpha-atc]');
        const stickyButton = this.querySelector('[data-alpha-sticky-btn]');
        const buyable = Boolean(variant && variant.available);

        if (input) {
          if (variant) input.value = variant.id;
          input.disabled = !buyable;
        }

        if (button) {
          const label = button.querySelector('[data-alpha-atc-text]');
          button.disabled = !buyable;

          if (label) {
            if (buyable) {
              label.textContent = button.dataset.labelAdd;
            } else if (variant) {
              label.textContent = button.dataset.labelSoldout;
            } else {
              // No variant matches this combination at all, which is a
              // different fact from "this one is sold out".
              label.textContent = button.dataset.labelUnavailable;
            }
          }
        }

        if (stickyButton) stickyButton.disabled = !buyable;
      }

      updateUrl(variant) {
        if (!variant || !window.history || !window.history.replaceState) return;

        // replaceState rather than pushState: a shopper cycling through six
        // sizes should not have to press Back six times to leave the page.
        window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
      }

      /* --------------------------------------------------------- gallery */

      bindGallery() {
        if (!this.stage || this.slides.length < 2) return;

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => {
            this.showMedia(thumb.dataset.mediaId);
          });
        });

        // The rail is the source of truth: a swipe and a thumbnail click both
        // end up changing scrollLeft, so the active thumbnail is derived from
        // the scroll position rather than tracked separately.
        let frame = null;
        this.stage.addEventListener(
          'scroll',
          () => {
            if (frame) window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(() => this.syncThumbs());
          },
          { passive: true }
        );
      }

      syncThumbs() {
        if (!this.stage || !this.thumbs.length) return;

        const middle = this.stage.scrollLeft + this.stage.clientWidth / 2;
        let activeId = null;

        this.slides.forEach((slide) => {
          const start = slide.offsetLeft - this.stage.offsetLeft;
          if (middle >= start && middle < start + slide.offsetWidth) {
            activeId = slide.dataset.mediaId;
          }
        });

        this.setActiveThumb(activeId);
      }

      setActiveThumb(mediaId) {
        if (!mediaId) return;

        this.thumbs.forEach((thumb) => {
          const isActive = thumb.dataset.mediaId === String(mediaId);
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }

      showMedia(mediaId, instant = false) {
        if (!this.stage || !mediaId) return;

        const slide = this.slides.find((item) => item.dataset.mediaId === String(mediaId));
        if (!slide) return;

        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

        const smooth = !instant && !this.prefersReducedMotion();

        this.stage.scrollTo({
          left: slide.offsetLeft - this.stage.offsetLeft,
          behavior: smooth ? 'smooth' : 'auto',
        });

        // Marked from the target, not read back from scrollLeft: a smooth
        // scroll has not moved yet at this point, so deriving it here would
        // flash the outgoing thumbnail for the length of the animation.
        this.setActiveThumb(mediaId);
      }

      updateMedia(variant) {
        if (!variant || !variant.media) return;
        this.showMedia(variant.media);
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      /* ------------------------------------------------------------ zoom */

      bindZoom() {
        if (this.dataset.zoom !== 'true') return;

        this.querySelectorAll('[data-alpha-zoom]').forEach((link) => {
          link.addEventListener('click', (event) => {
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

            event.preventDefault();
            this.openZoom(link.getAttribute('href'), link.dataset.zoomAlt || '');
          });
        });
      }

      openZoom(src, alt) {
        if (!src) return;

        this.closeZoom();
        this.zoomOpener = document.activeElement;

        const overlay = document.createElement('div');
        overlay.className = 'alpha-zoom';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-label', alt);

        const image = document.createElement('img');
        image.className = 'alpha-zoom__image';
        image.src = src;
        image.alt = alt;

        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'alpha-zoom__close';
        close.setAttribute('aria-label', this.dataset.closeLabel || 'Close');

        // The close icon comes from the shared alpha-icon set, rendered into a
        // <template> by Liquid and cloned here. Drawing a second close glyph in
        // JavaScript is how a theme ends up with four stroke weights.
        const icon = this.querySelector('template[data-alpha-close-icon]');
        if (icon) close.appendChild(icon.content.cloneNode(true));

        overlay.appendChild(image);
        overlay.appendChild(close);
        document.body.appendChild(overlay);

        this.zoomOverlay = overlay;

        this.onZoomKey = (event) => {
          if (event.key === 'Escape') {
            this.closeZoom();
            return;
          }

          // aria-modal="true" is a promise that focus cannot wander off behind
          // the overlay, and the browser does not keep that promise on its own.
          // The close button is the only focusable thing in here, so Tab in
          // either direction simply stays on it.
          if (event.key === 'Tab') {
            event.preventDefault();
            close.focus();
          }
        };

        overlay.addEventListener('click', () => this.closeZoom());
        document.addEventListener('keydown', this.onZoomKey);
        // Without this the page carries on scrolling behind the lightbox, so
        // closing it drops the shopper somewhere they never navigated to.
        document.body.classList.add('overflow-hidden');
        close.focus();
      }

      closeZoom() {
        if (this.onZoomKey) {
          document.removeEventListener('keydown', this.onZoomKey);
          this.onZoomKey = null;
        }

        if (this.zoomOverlay) {
          document.body.classList.remove('overflow-hidden');
          this.zoomOverlay.remove();
          this.zoomOverlay = null;
        }

        if (this.zoomOpener && typeof this.zoomOpener.focus === 'function') {
          this.zoomOpener.focus();
          this.zoomOpener = null;
        }
      }

      /* -------------------------------------------------------- quantity */

      bindQuantity() {
        const control = this.querySelector('[data-alpha-qty]');
        if (!control) return;

        const input = control.querySelector('[data-alpha-qty-input]');
        if (!input) return;

        control.querySelectorAll('[data-alpha-qty-step]').forEach((button) => {
          button.addEventListener('click', () => {
            const step = parseInt(button.dataset.alphaQtyStep, 10) || 1;
            const min = parseInt(input.min, 10) || 1;
            const current = parseInt(input.value, 10) || min;

            input.value = Math.max(min, current + step);
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });

        // A shopper can still type into the field, so a stray "0" or "-3" has to
        // be corrected on the way out rather than rejected at the checkout.
        input.addEventListener('change', () => {
          const min = parseInt(input.min, 10) || 1;
          const value = parseInt(input.value, 10);
          if (!value || value < min) input.value = min;
        });
      }

      /* ------------------------------------------------------ sticky bar */

      bindStickyBar() {
        const bar = this.querySelector('[data-alpha-sticky]');
        const anchor = this.querySelector('[data-alpha-atc-anchor]');
        const mainButton = this.querySelector('[data-alpha-atc]');
        const stickyButton = this.querySelector('[data-alpha-sticky-btn]');

        // No add-to-cart block on the page means there is nothing for the bar to
        // duplicate, so it stays hidden rather than becoming a dead control.
        if (!bar || !anchor || !mainButton) return;

        if (stickyButton) {
          stickyButton.addEventListener('click', () => {
            // Clicking the real button keeps product-form.js in charge of the
            // AJAX add and the cart drawer, rather than reimplementing either.
            mainButton.click();
          });
          stickyButton.disabled = mainButton.disabled;
        }

        bar.hidden = false;

        if (!('IntersectionObserver' in window)) {
          // Without the observer the bar would sit over the page permanently,
          // covering the very button it mirrors. Leaving it off is the safer
          // failure: the real button is still there.
          bar.hidden = true;
          return;
        }

        // This observer watches a control, not a reveal animation - scroll
        // reveals stay with alpha-motion.js.
        this.stickyObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // `!isIntersecting` on its own is also true while the real button
              // is still BELOW the fold, which on a phone is the whole of the
              // first screen - the bar used to slide up on load and cover the
              // page before the shopper had ever seen the button it mirrors.
              // It may only appear once the button has left through the top.
              const scrolledPast = entry.boundingClientRect.top < 0;
              bar.classList.toggle('is-in', !entry.isIntersecting && scrolledPast);
            });
          },
          { rootMargin: '0px 0px -40px 0px' }
        );

        this.stickyObserver.observe(anchor);
      }
    }
  );
}
